import React, { useState, useCallback, useEffect } from 'react';
import { Text, ScrollbarStyles } from 'hyarchis-global';
import { updateGroupsStorage as updateGroupsStorageAction } from 'store/groups/actions';
import { AxiosError } from 'axios';
import { openErrorNotification } from 'hyarchis-global/components/notifications';
import { useActions, useShallowSelector } from 'hooks';
import { ApplicationState } from 'store/types';
import { Button, Divider } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { CloseOutlined } from '@ant-design/icons';
import { DocumentGroup } from 'types/documentGroup';
import styled from 'styled-components';
import Styled from '../listItemHead.styled';
import StyledDrawers from './drawers.styled';

interface OwnProps {
  setOpenDrawer: boolean;
  onClose: () => void;
  documentGroup: DocumentGroup;
  onDocumentGroupChange: (data: DocumentGroup) => void;
}

const { SubTitleWrapper, StyledTextAreaWrapper } = Styled;
const { StyledDrawer, FormGroup } = StyledDrawers;

const StyledScrollContainer = styled.div`
  max-height: 800px;
  overflow: auto;
  ${ScrollbarStyles}
`;

const StyledShortButton = styled(Button)`
  margin-top: 15px;
  width: 92.5%;
`;

const DefinedInputSettingsDrawer = ({
  setOpenDrawer,
  onClose,
  documentGroup,
  onDocumentGroupChange,
}: OwnProps) => {
  const [openDefinedSettings, setDefinedSettingsOpen] = useState(setOpenDrawer);
  const { stateGroups } = useShallowSelector(({ groups }: ApplicationState) => ({
    stateGroups: groups,
  }));

  const [documentGroupsState, setDocumentGroups] = useState<DocumentGroup[]>(
    stateGroups.documentGroups,
  );

  const [externalSourceData, setExternalSourceData] = useState<string>(
    documentGroup?.externalDataSource?.toString() || '',
  );

  const [documentIds, setDocumentIdsToExtract] = useState<string[]>(
    documentGroup?.documentIdsToExtract || [],
  );

  const handleExternalSourceChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setExternalSourceData(event.target.value);
  };

  const handleDocumentValuesChange = useCallback(
    (index: number, newValue: string) => {
      const updatedValues = [...documentIds];
      updatedValues[index] = newValue;
      setDocumentIdsToExtract(updatedValues);
    },
    [documentIds],
  );

  const handleDocumentGroupChange = (documentGroupChange: DocumentGroup) => {
    onDocumentGroupChange(documentGroupChange);
  };

  const handleClose = () => {
    setDefinedSettingsOpen(false);
    onClose();
  };

  const { updateGroupsStorage } = useActions({
    updateGroupsStorage: updateGroupsStorageAction,
  });

  const addDocument = () => {
    setDocumentIdsToExtract([...documentIds, '']);
  };

  const removeInput = (index: number) => {
    const updatedValues = [...documentIds];
    updatedValues.splice(index, 1);
    setDocumentIdsToExtract(updatedValues);
  };

  const onSaveDefinedInputs = () => {
    try {
      let sourceString = '';
      if (externalSourceData !== '') {
        const source = JSON.parse(externalSourceData);
        sourceString = JSON.stringify(source);
      }

      const updatedDocumentsGroup = documentGroupsState.map((group) => {
        if (group.id === documentGroup.id) {
          return {
            ...group,
            externalDataSource: sourceString,
            documentIdsToExtract: documentIds,
          };
        }

        return group;
      });

      setDocumentGroups(updatedDocumentsGroup);
      const updatedDocumentGroup = updatedDocumentsGroup.find(
        (group) => group.id === documentGroup.id,
      );

      if (updatedDocumentGroup) {
        handleDocumentGroupChange(updatedDocumentGroup);
      }
      handleClose();
    } catch (error) {
      openErrorNotification((error as AxiosError).message);
    }
  };

  useEffect(() => {
    setDocumentGroups(stateGroups.documentGroups);
  }, [stateGroups.documentGroups]);

  useEffect(() => {
    setExternalSourceData(documentGroup.externalDataSource || '');
  }, [documentGroup.externalDataSource]);

  useEffect(() => {
    setDocumentIdsToExtract(documentGroup.documentIdsToExtract || []);
  }, [documentGroup.documentIdsToExtract]);

  useEffect(() => {
    updateGroupsStorage(documentGroupsState);
  }, [updateGroupsStorage, documentGroupsState]);

  useEffect(() => {
    setDefinedSettingsOpen(setOpenDrawer);
  }, [setOpenDrawer]);

  return (
    <SubTitleWrapper>
      <StyledDrawer
        width={700}
        title={<span>Defined input settings</span>}
        visible={openDefinedSettings}
        closable
        onClose={handleClose}
      >
        <FormGroup>
          <StyledScrollContainer>
            External source data json:
            <StyledTextAreaWrapper>
              <TextArea
                value={externalSourceData.toString()}
                onChange={handleExternalSourceChange}
              />
            </StyledTextAreaWrapper>
            <Divider style={{ fontWeight: 'bold' }} />
            Document ids to extract:
            {documentIds.map((value, index) => (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <Text.Validatable
                  value={value}
                  onChange={(e) => handleDocumentValuesChange(index, e?.toString() || '')}
                  style={{ minWidth: '98%' }}
                />
                {index > 0 && (
                  <Button
                    size='small'
                    type='text'
                    onClick={() => removeInput(index)}
                    style={{ padding: '5px', minWidth: '30px' }}
                  >
                    <CloseOutlined />
                  </Button>
                )}
              </div>
            ))}
            <StyledShortButton type='primary' onClick={addDocument}>
              Add More Document Ids
            </StyledShortButton>
            <StyledShortButton type='primary' onClick={() => onSaveDefinedInputs()}>
              Save Defined Input Settings
            </StyledShortButton>
          </StyledScrollContainer>
        </FormGroup>
      </StyledDrawer>
    </SubTitleWrapper>
  );
};

export default React.memo(DefinedInputSettingsDrawer);
